import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Info, 
  Package, 
  Zap, 
  Newspaper, 
  Award, 
  Image as ImageIcon, 
  LogOut, 
  ChevronRight,
  User,
  MessageSquare,
  Layers,
  FileText,
  Briefcase,
  TrendingUp,
  Menu,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleToggle = (e) => setIsModalOpen(e.detail);
    window.addEventListener('admin-modal-toggle', handleToggle);
    return () => window.removeEventListener('admin-modal-toggle', handleToggle);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'About', path: '/admin/about', icon: Info },
    { name: 'Growth Stats', path: '/admin/growth-stats', icon: TrendingUp },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Manage Strengths', path: '/admin/strengths', icon: Zap },
    { name: 'News', path: '/admin/news', icon: Newspaper },
    { name: 'Awards', path: '/admin/awards', icon: Award },
    { name: 'Gallery', path: '/admin/gallery', icon: ImageIcon },
    { name: 'Divisions', path: '/admin/divisions', icon: Layers },
    { name: 'Download documents', path: '/admin/downloads', icon: FileText },
    { name: 'Careers', path: '/admin/careers', icon: Briefcase },
    { name: 'Inquiries', path: '/admin/inquiries', icon: MessageSquare },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50 font-poppins">
      
      {/* ── Desktop Sidebar (Large Screens >= lg) ── */}
      {!isModalOpen && (
        <aside className="w-72 bg-sugarcane-charcoal text-white hidden lg:flex flex-col fixed h-screen z-30">
          <div className="p-8 border-b border-white/10">
            <Link to="/" className="flex items-center space-x-2">
              <div className="bg-sugarcane-green p-1.5 rounded-lg">
                <User className="text-white w-5 h-5" />
              </div>
              <span className="text-xl font-montserrat font-bold tracking-tight">
                ADMIN<span className="text-sugarcane-green">PANEL</span>
              </span>
            </Link>
          </div>

          <nav className="flex-grow p-6 space-y-2 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-300 group ${
                  location.pathname === item.path 
                  ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10' 
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <item.icon size={22} className={location.pathname === item.path ? 'text-white' : 'group-hover:text-sugarcane-green'} />
                  <span className="font-bold tracking-wide text-sm">{item.name}</span>
                </div>
                <ChevronRight size={16} className={`transition-transform duration-300 ${location.pathname === item.path ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-hover:translate-x-1'}`} />
              </Link>
            ))}
          </nav>

          <div className="p-8 border-t border-white/10">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center space-x-4 text-gray-400 hover:text-white transition-colors group cursor-pointer"
            >
              <LogOut size={22} />
              <span className="font-bold text-left text-sm">Logout Session</span>
            </button>
          </div>
        </aside>
      )}

      {/* ── Mobile Sidebar Drawer (Small & Medium Screens < lg) ── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 lg:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed inset-y-0 left-0 w-72 bg-sugarcane-charcoal text-white z-50 lg:hidden flex flex-col h-full shadow-2xl"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <Link to="/" className="flex items-center space-x-2">
                  <div className="bg-sugarcane-green p-1.5 rounded-lg">
                    <User className="text-white w-5 h-5" />
                  </div>
                  <span className="text-xl font-montserrat font-bold tracking-tight">
                    ADMIN<span className="text-sugarcane-green">PANEL</span>
                  </span>
                </Link>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="p-1 rounded-lg text-gray-400 hover:text-white"
                >
                  <X size={22} />
                </button>
              </div>

              <nav className="flex-grow p-4 space-y-1.5 overflow-y-auto">
                {menuItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-300 ${
                      location.pathname === item.path 
                      ? 'bg-sugarcane-green text-white shadow-md' 
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon size={20} className={location.pathname === item.path ? 'text-white' : 'text-sugarcane-green'} />
                      <span className="font-bold text-xs tracking-wide">{item.name}</span>
                    </div>
                    <ChevronRight size={14} />
                  </Link>
                ))}
              </nav>

              <div className="p-6 border-t border-white/10">
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <LogOut size={20} />
                  <span className="font-bold text-xs">Logout Session</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── Main Content Area ── */}
      <main className={`flex-grow min-h-screen ${!isModalOpen ? 'lg:ml-72' : ''} w-full overflow-x-hidden`}>
        {!isModalOpen && (
          <header className="bg-white border-b border-gray-100 py-4 sm:py-6 px-4 sm:px-8 flex justify-between items-center sticky top-0 z-40 shadow-sm">
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sugarcane-charcoal lg:hidden cursor-pointer"
                aria-label="Open Admin Menu"
              >
                <Menu size={20} />
              </button>
              <h2 className="text-lg sm:text-2xl font-extrabold text-sugarcane-charcoal truncate">
                {menuItems.find(m => m.path === location.pathname)?.name || 'Admin'}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-sugarcane-charcoal">Administrator</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest font-black">Super User</p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-100 rounded-xl flex items-center justify-center border border-gray-200 flex-shrink-0">
                <User className="text-sugarcane-charcoal" size={18} />
              </div>
            </div>
          </header>
        )}

        <div className="p-4 sm:p-6 lg:p-10 max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
